/**
 * Location Autocomplete
 *
 * Search input with live city suggestions from Photon API.
 * Shows popular VC hub cities when empty, API results when typing.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { Search, X, MapPin, Globe, Check } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { noOutlineStyle } from '@/lib/platform';
import {
  useLocationAutocomplete,
  POPULAR_LOCATIONS,
  type LocationSuggestion,
} from '@/lib/geo/location-autocomplete';

// ============================================
// Types
// ============================================

interface LocationAutocompleteProps {
  selectedLocations: string[];
  onToggleLocation: (value: string) => void;
}

// ============================================
// Component
// ============================================

export function LocationAutocomplete({
  selectedLocations,
  onToggleLocation,
}: LocationAutocompleteProps) {
  const colors = useThemeColors();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input (300ms)
  useEffect(() => {
    if (searchQuery.length < 2) {
      setDebouncedQuery('');
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: suggestions, isFetching } = useLocationAutocomplete(debouncedQuery);

  // Determine what to show in dropdown
  const hasApiResults = debouncedQuery.length >= 2 && suggestions && suggestions.length > 0;
  const showPopular = isFocused && debouncedQuery.length < 2;
  const showResults = isFocused && debouncedQuery.length >= 2;
  const noResults = showResults && !isFetching && suggestions?.length === 0;

  // Items to render
  const displayItems = showPopular
    ? POPULAR_LOCATIONS
    : hasApiResults
      ? suggestions ?? []
      : [];

  const handleSelect = (suggestion: LocationSuggestion) => {
    onToggleLocation(suggestion.displayName);
    setSearchQuery('');
    setDebouncedQuery('');
  };

  const handleBlur = () => {
    // Delay blur so tap events on dropdown items can fire first
    blurTimeoutRef.current = setTimeout(() => setIsFocused(false), 150);
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setIsFocused(true);
  };

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const hasRemote = selectedLocations.includes('Remote');
  const cityLocations = selectedLocations.filter(l => l !== 'Remote');

  return (
    <View>
      {/* Selected pills */}
      {selectedLocations.length > 0 && (
        <View className="flex-row flex-wrap mb-2" style={{ gap: 6 }}>
          {/* Remote first if active */}
          {hasRemote && (
            <Pressable
              onPress={() => onToggleLocation('Remote')}
              className="flex-row items-center"
              style={{
                paddingHorizontal: 10,
                paddingVertical: 5,
                borderRadius: 999,
                backgroundColor: colors.primary[50],
                borderWidth: 1,
                borderColor: colors.primary[200],
                gap: 4,
              }}
            >
              <Globe size={12} color={colors.primary[700]} />
              <Text
                style={{ color: colors.primary[700] }}
                className="text-xs font-medium"
              >
                Remote
              </Text>
              <X size={12} color={colors.primary[700]} />
            </Pressable>
          )}

          {/* City pills */}
          {cityLocations.map(location => (
            <Pressable
              key={location}
              onPress={() => onToggleLocation(location)}
              className="flex-row items-center"
              style={{
                paddingHorizontal: 10,
                paddingVertical: 5,
                borderRadius: 999,
                backgroundColor: colors.primary[50],
                borderWidth: 1,
                borderColor: colors.primary[200],
                gap: 4,
              }}
            >
              <MapPin size={12} color={colors.primary[700]} />
              <Text
                style={{ color: colors.primary[700] }}
                className="text-xs font-medium"
                numberOfLines={1}
              >
                {location.split(',')[0]}
              </Text>
              <X size={12} color={colors.primary[700]} />
            </Pressable>
          ))}
        </View>
      )}

      {/* Remote toggle */}
      <Pressable
        onPress={() => onToggleLocation('Remote')}
        className="flex-row items-center mb-2"
        style={{
          paddingHorizontal: 10,
          paddingVertical: 6,
          borderRadius: 999,
          backgroundColor: hasRemote ? colors.primary[50] : colors.surface.secondary,
          borderWidth: 1,
          borderColor: hasRemote ? colors.primary[200] : 'transparent',
          alignSelf: 'flex-start',
        }}
      >
        <Globe size={13} color={hasRemote ? colors.primary[700] : colors.text.secondary} />
        <Text
          style={{
            color: hasRemote ? colors.primary[700] : colors.text.secondary,
            marginLeft: 5,
          }}
          className="text-xs font-medium"
        >
          Remote
        </Text>
      </Pressable>

      {/* Search input */}
      <View className="relative">
        <View
          className="flex-row items-center px-3 rounded-lg"
          style={{
            height: 40,
            backgroundColor: colors.surface.secondary,
            borderWidth: 1,
            borderColor: isFocused ? colors.primary[300] : colors.border.light,
          }}
        >
          <Search size={16} color={colors.text.tertiary} />
          <TextInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="Type a city..."
            placeholderTextColor={colors.text.tertiary}
            className="flex-1 text-sm mx-2"
            style={noOutlineStyle({ color: colors.text.primary })}
            autoCorrect={false}
            autoCapitalize="words"
          />
          {isFetching && debouncedQuery.length >= 2 && (
            <ActivityIndicator size="small" color={colors.primary[600]} />
          )}
          {searchQuery.length > 0 && !isFetching && (
            <Pressable
              onPress={() => {
                setSearchQuery('');
                setDebouncedQuery('');
              }}
              hitSlop={8}
            >
              <X size={16} color={colors.text.tertiary} />
            </Pressable>
          )}
        </View>

        {/* Dropdown */}
        {isFocused && (showPopular || showResults) && (
          <Animated.View
            entering={FadeIn.duration(150)}
            className="absolute left-0 right-0 rounded-xl overflow-hidden z-50"
            style={{
              top: 44,
              maxHeight: 220,
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.12,
              shadowRadius: 12,
              elevation: 5,
            }}
          >
            <ScrollView
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
            >
              {/* Section header */}
              {showPopular && (
                <Text
                  className="text-xs font-medium uppercase px-3 pt-2.5 pb-1"
                  style={{ color: colors.text.tertiary, letterSpacing: 0.6 }}
                >
                  Suggested
                </Text>
              )}

              {/* No results */}
              {noResults && !isFetching && (
                <View className="px-3 py-4 items-center">
                  <Text className="text-sm" style={{ color: colors.text.tertiary }}>
                    No cities found
                  </Text>
                </View>
              )}

              {/* Suggestion rows */}
              {displayItems.map((item, index) => {
                const isSelected = selectedLocations.includes(item.displayName);
                return (
                  <Pressable
                    key={item.id}
                    onPress={() => handleSelect(item)}
                    className="flex-row items-center px-3 py-2.5"
                    style={{
                      backgroundColor: isSelected ? colors.primary[50] : 'transparent',
                      borderTopWidth: index > 0 || showPopular ? 1 : 0,
                      borderColor: colors.border.light,
                    }}
                  >
                    <MapPin size={15} color={colors.text.tertiary} />
                    <View className="flex-1 ml-2.5">
                      <Text
                        className="text-sm font-medium"
                        style={{ color: colors.text.primary }}
                        numberOfLines={1}
                      >
                        {item.city}
                      </Text>
                      {item.country !== item.city && (
                        <Text
                          className="text-xs"
                          style={{ color: colors.text.tertiary }}
                          numberOfLines={1}
                        >
                          {item.state ? `${item.state}, ${item.country}` : item.country}
                        </Text>
                      )}
                    </View>
                    {isSelected && (
                      <Check size={16} color={colors.primary[600]} />
                    )}
                  </Pressable>
                );
              })}
            </ScrollView>
          </Animated.View>
        )}
      </View>
    </View>
  );
}
